const CURRENT_YEAR = new Date().getFullYear();

/** Citizen -> nationality name; non-citizen -> `Non-{nationalityName}`. */
export function buildRegisterNationality(nationalityName, isCitizen) {
  const name = String(nationalityName ?? "").trim();
  if (!name) return "";
  return isCitizen ? name : `Non-${name}`;
}

/** Parse nationality from URL or form (`Non-{nationality}` vs nationality name). */
export function parseRegisterNationality(nationalityValue) {
  const param = String(nationalityValue ?? "").trim();
  if (!param) return null;
  if (/^non-/i.test(param)) {
    const countryName = param.replace(/^non-/i, "").trim();
    return {
      mode: "non-citizen",
      countryName,
      formNationality: param,
    };
  }
  return {
    mode: "citizen",
    countryName: param,
    formNationality: param,
  };
}

export function isNonCitizenNationality(nationality) {
  return /^non-/i.test(String(nationality ?? "").trim());
}

/** Resolve is_citizen for patient Demographic from URL param, stored value, or nationality. */
export function resolvePatientIsCitizen({ urlParam, nationality, stored }) {
  if (stored === true || stored === false) return stored;
  if (urlParam === "true") return true;
  if (urlParam === "false") return false;
  if (nationality) return !isNonCitizenNationality(nationality);
  return undefined;
}

export function withPatientIsCitizen(demographic, ctx = {}) {
  const isCitizen = resolvePatientIsCitizen({
    urlParam: ctx.urlParam,
    nationality: demographic?.nationality,
    stored: ctx.stored,
  });
  if (typeof isCitizen === "boolean") {
    return { ...demographic, is_citizen: isCitizen };
  }
  return demographic;
}

/** Nationality label from DB (`nationality_name`), fallback to `country_name`. */
export function getCountryNationalityName(country) {
  return String(country?.nationality_name || country?.country_name || "").trim();
}

function isSameCountry(a, b) {
  const codeA = String(a?.country_code ?? "").trim();
  const codeB = String(b?.country_code ?? "").trim();
  return Boolean(codeA) && codeA === codeB;
}

/**
 * `isCitizen` stays tied to the ID (citizen vs resident of the issuing country).
 * Nationality is relative to the country of residence: only a citizen ID issued
 * by the residence country yields its nationality (e.g. "Saudi"); a residence ID,
 * or any ID from another country, yields `Non-{residence nationality}` (e.g. "Non-Saudi").
 */
export function resolveResidenceNationality({
  issuingCountry,
  residenceCountry,
  isCitizen,
}) {
  const isResidenceCitizen =
    Boolean(isCitizen) && isSameCountry(issuingCountry, residenceCountry);
  const issuingCountryName = String(issuingCountry?.country_name ?? "").trim();
  const residenceCountryName = String(
    residenceCountry?.country_name ?? ""
  ).trim();
  return {
    isCitizen: Boolean(isCitizen),
    nationality: buildRegisterNationality(
      getCountryNationalityName(residenceCountry),
      isResidenceCitizen
    ),
    countryOfOrigin: isResidenceCitizen
      ? residenceCountryName
      : isCitizen
      ? issuingCountryName
      : "",
  };
}

const GCC_COUNTRY_CODE_TO_TYPE = {
  SA: "SA",
  AE: "AE",
  OM: "OM",
  BH: "BH",
  QA: "QA",
  KW: "KW",
  "01": "SA",
  "02": "AE",
  "03": "OM",
  "04": "BH",
  "05": "QA",
  "06": "KW",
};

function getCountrySearchText(country) {
  return [
    country?.country_name,
    country?.nationality_name,
    getCountryNationalityName(country),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function getGccCountryType(country) {
  if (!country) return null;
  if (country.is_saudi) return "SA";

  const code = String(country.country_code ?? "").trim().toUpperCase();
  if (GCC_COUNTRY_CODE_TO_TYPE[code]) {
    return GCC_COUNTRY_CODE_TO_TYPE[code];
  }

  const name = getCountrySearchText(country);
  if (name.includes("saudi")) return "SA";
  if (
    name.includes("emirates") ||
    name.includes("emirati") ||
    name.includes("uae") ||
    name.includes("united arab")
  ) {
    return "AE";
  }
  if (name.includes("oman") || name.includes("omani")) return "OM";
  if (name.includes("bahrain") || name.includes("bahraini")) return "BH";
  if (name.includes("qatar") || name.includes("qatari")) return "QA";
  if (name.includes("kuwait") || name.includes("kuwaiti")) return "KW";
  return null;
}

export function findCountryByCode(countries, countryCode) {
  const code = String(countryCode ?? "").trim();
  return countries.find((c) => String(c.country_code ?? "").trim() === code);
}

export function getNationalIdFieldConfig(country, isCitizen) {
  const type = getGccCountryType(country);
  switch (type) {
    case "SA":
      return {
        type: "SA",
        minLength: 10,
        maxLength: 10,
        hint: "10 digits; must start with 1 or 2",
      };
    case "AE":
      return {
        type: "AE",
        minLength: 15,
        maxLength: 15,
        hint: "15 digits; must start with 784",
      };
    case "OM":
      return {
        type: "OM",
        minLength: isCitizen ? 8 : 8,
        maxLength: isCitizen ? 8 : 12,
        hint: isCitizen
          ? "8 digits (Oman citizen)"
          : "8 to 12 digits (Oman resident)",
      };
    case "BH":
      return {
        type: "BH",
        minLength: 9,
        maxLength: 9,
        hint: "9 digits (YYMM + sequence)",
      };
    case "QA":
      return {
        type: "QA",
        minLength: 11,
        maxLength: 11,
        hint: "11 digits; first digit 1–3",
      };
    case "KW":
      return {
        type: "KW",
        minLength: 12,
        maxLength: 12,
        hint: "12 digits; starts with 2 or 3",
      };
    default:
      return {
        type: null,
        minLength: 8,
        maxLength: 15,
        hint: country ? "" : "Select a nationality first",
      };
  }
}

function isNumericOnly(value) {
  return /^\d+$/.test(value);
}

function isAllZeros(value) {
  return /^0+$/.test(value);
}

function isRepeatingSingleDigit(value) {
  return /^(\d)\1+$/.test(value);
}

function isBirthYearInRange(year) {
  return year >= 1900 && year <= CURRENT_YEAR;
}

function isValidCalendarDate(year, month, day) {
  if (month < 1 || month > 12 || day < 1 || day > 31) return false;
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function inferFullYearFromYY(yy) {
  const currentYY = CURRENT_YEAR % 100;
  return yy > currentYY ? 1900 + yy : 2000 + yy;
}

/** Sanitize user input for national ID field. */
export function sanitizeNationalIdInput(value, config) {
  if (!config?.type) {
    return value.replace(/\D/g, "").slice(0, config?.maxLength || 15);
  }

  let digits = String(value ?? "").replace(/\D/g, "");

  if (config.type === "SA") {
    if (digits.length === 0) return "";
    if (!/^[12]/.test(digits)) return "";
    return digits.slice(0, 10);
  }

  if (config.type === "AE") {
    if (digits.length === 0) return "";
    if (!digits.startsWith("784")) {
      if ("784".startsWith(digits)) return digits;
      return "";
    }
    return digits.slice(0, 15);
  }

  if (config.type === "KW") {
    if (digits.length === 0) return "";
    if (!/^[23]/.test(digits)) {
      if ("2".startsWith(digits) || "3".startsWith(digits)) return digits.charAt(0);
      return "";
    }
    return digits.slice(0, 12);
  }

  if (config.type === "QA") {
    if (digits.length === 0) return "";
    if (!/^[1-3]/.test(digits.charAt(0))) return "";
    return digits.slice(0, 11);
  }

  return digits.slice(0, config.maxLength);
}

export function validateNationalIdValue(value, country, isCitizen) {
  const config = getNationalIdFieldConfig(country, isCitizen);
  const id = String(value ?? "").replace(/[\s-]/g, "");

  if (!country || !config.type) {
    return { valid: false, message: "Please select a country" };
  }

  if (!id) {
    return { valid: false, message: "Required" };
  }

  if (id.length < config.minLength || id.length > config.maxLength) {
    return {
      valid: false,
      message: `National ID must be ${config.minLength}${
        config.minLength !== config.maxLength
          ? ` to ${config.maxLength}`
          : ""
      } digits`,
    };
  }

  if (!isNumericOnly(id)) {
    return { valid: false, message: "National ID must contain digits only" };
  }

  switch (config.type) {
    case "SA":
      if (id[0] !== "1" && id[0] !== "2") {
        return {
          valid: false,
          message: "Saudi National ID must start with 1 or 2",
        };
      }
      return { valid: true, message: "" };

    case "AE":
      if (!id.startsWith("784")) {
        return {
          valid: false,
          message: "UAE Emirates ID must start with 784",
        };
      }
      {
        const birthYear = parseInt(id.slice(3, 7), 10);
        if (!isBirthYearInRange(birthYear)) {
          return { valid: false, message: "Invalid birth year in Emirates ID" };
        }
      }
      return { valid: true, message: "" };

    case "OM":
      if (isAllZeros(id) || isRepeatingSingleDigit(id)) {
        return { valid: false, message: "Invalid Oman Civil ID" };
      }
      return { valid: true, message: "" };

    case "BH": {
      const mm = parseInt(id.slice(2, 4), 10);
      if (mm < 1 || mm > 12) {
        return { valid: false, message: "Invalid month in Bahrain CPR" };
      }
      const yy = parseInt(id.slice(0, 2), 10);
      if (!isBirthYearInRange(inferFullYearFromYY(yy))) {
        return { valid: false, message: "Invalid year in Bahrain CPR" };
      }
      return { valid: true, message: "" };
    }

    case "QA": {
      const firstDigit = parseInt(id[0], 10);
      if (firstDigit < 1 || firstDigit > 3) {
        return {
          valid: false,
          message: "Qatar ID first digit must be 1, 2, or 3",
        };
      }
      const birthYear = parseInt(id.slice(1, 5), 10);
      if (!isBirthYearInRange(birthYear)) {
        return { valid: false, message: "Invalid birth year in Qatar ID" };
      }
      return { valid: true, message: "" };
    }

    case "KW": {
      if (id[0] !== "2" && id[0] !== "3") {
        return {
          valid: false,
          message: "Kuwait Civil ID must start with 2 or 3",
        };
      }
      const centuryBase = id[0] === "2" ? 1900 : 2000;
      const yy = parseInt(id.slice(1, 3), 10);
      const mm = parseInt(id.slice(3, 5), 10);
      const dd = parseInt(id.slice(5, 7), 10);
      if (!isValidCalendarDate(centuryBase + yy, mm, dd)) {
        return { valid: false, message: "Invalid date in Kuwait Civil ID" };
      }
      return { valid: true, message: "" };
    }

    default:
      return { valid: false, message: "Unsupported country for National ID" };
  }
}
